import React from "react";
import { Box, Button, Checkbox, Typography } from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import FilterSearchInput from "./FilterSearchInput";
import { orderOptionsSelectedFirst } from "./pictorialUtils";

// Lazy-load filter dropdowns: show first N by default; when user searches, show all matches
const FILTER_DROPDOWN_INITIAL_LIMIT = 100;

/**
 * Dropdown-style filter with checkboxes, search and Select All.
 * Selection state lives in the parent: `onSelectionChange(field, newSet)` is called
 * with the full next Set whenever a checkbox or Select All is toggled.
 */
const FilterDropdown = ({
	field,
	label,
	options, // master option list for this field
	validOptions, // currently valid (dependency-narrowed) options
	selectedSet,
	isOpen,
	searchTerm,
	onToggle,
	onSearchChangeDebounced,
	onSelectionChange,
	alwaysShowSearch = false,
}) => {
	// Filter options based on valid options (always show options, just filter them)
	const enabledOptions = options.filter((option) => validOptions.includes(option));

	const filteredOptionsRaw = enabledOptions.filter((option) =>
		option.toLowerCase().includes(searchTerm.toLowerCase())
	);
	const filteredOptions = orderOptionsSelectedFirst(filteredOptionsRaw, selectedSet);

	// Lazy display: without search show first 100 + any selected not in first 100; with search show all matches
	const hasSearch = searchTerm.trim() !== "";
	const optionsToDisplay =
		hasSearch
			? filteredOptions
			: (() => {
					const first = filteredOptions.slice(0, FILTER_DROPDOWN_INITIAL_LIMIT);
					const selectedNotInFirst = Array.from(selectedSet).filter(
						(o) => filteredOptions.includes(o) && !first.includes(o)
					);
					return [...first, ...selectedNotInFirst];
				})();
	const isTruncated = !hasSearch && filteredOptions.length > FILTER_DROPDOWN_INITIAL_LIMIT;

	const handleCheckboxChange = (option, checked) => {
		const newSet = new Set(selectedSet);
		if (checked) {
			newSet.add(option);
		} else {
			newSet.delete(option);
		}
		onSelectionChange(field, newSet);
	};

	const handleSelectAll = () => {
		const selectedInDisplay = Array.from(selectedSet).filter((option) => optionsToDisplay.includes(option));
		const allSelected = optionsToDisplay.length > 0 && selectedInDisplay.length === optionsToDisplay.length;
		const newSet = allSelected ? new Set() : new Set(optionsToDisplay);
		onSelectionChange(field, newSet);
	};

	const getDisplayText = () => {
		if (selectedSet.size === 0) return `${label}`;
		if (selectedSet.size === 1) return Array.from(selectedSet)[0];
		return `${selectedSet.size} selected`;
	};

	return (
		<Box sx={{ position: "relative", width: "100%" }} data-dropdown>
			{/* Dropdown trigger button */}
			<Button
				onClick={() => onToggle(field)}
				variant="outlined"
				fullWidth
				sx={{
					justifyContent: "space-between",
					textTransform: "none",
					fontSize: "0.6875rem",
					height: "40px",
					backgroundColor: "#f8f8f8",
					borderColor: isOpen ? "#000000" : "#000000",
					borderWidth: isOpen ? "2px" : "1px",
					color: selectedSet.size > 0 ? "#000000" : "#666666",
					fontWeight: selectedSet.size > 0 ? 500 : 400,
					"&:hover": {
						borderColor: "#000000",
						borderWidth: "2px",
						backgroundColor: "#f0f0f0",
					},
					"& .MuiButton-endIcon": {
						transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
						transition: "transform 0.2s ease-in-out",
					},
				}}
				endIcon={<ExpandMoreIcon />}>
				{getDisplayText()}
			</Button>

			{/* Dropdown content */}
			{isOpen && (
				<Box
					sx={{
						position: "absolute",
						top: "100%",
						left: 0,
						right: 0,
						zIndex: 1300,
						backgroundColor: "#f8f8f8",
						border: "1px solid #666666",
						borderRadius: "4px",
						boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
						mt: 0.5,
						maxHeight: "300px",
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
					}}>
					{/* Search bar: local state for instant typing, debounced parent update to reduce lag */}
					{(alwaysShowSearch || enabledOptions.length > 5) && (
						<Box sx={{ p: 1, borderBottom: "1px solid #e0e0e0" }}>
							<FilterSearchInput
								field={field}
								label={label}
								value={searchTerm}
								onSearchChangeDebounced={onSearchChangeDebounced}
								sx={{
									"& .MuiOutlinedInput-root": {
										fontSize: "0.6rem",
										height: "32px",
										backgroundColor: "#ffffff",
										"& fieldset": { borderColor: "#e0e0e0" },
										"&:hover fieldset": { borderColor: "#1976d2" },
										"&.Mui-focused fieldset": { borderColor: "#1976d2", borderWidth: 1 },
									},
								}}
							/>
						</Box>
					)}

					{/* Select All checkbox */}
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							px: 1,
							py: 0.5,
							borderBottom: "1px solid #666666",
							backgroundColor: "#ffffff",
						}}>
						<Checkbox
							checked={
								optionsToDisplay.length > 0 &&
								optionsToDisplay.length ===
									Array.from(selectedSet).filter((option) => optionsToDisplay.includes(option)).length
							}
							indeterminate={
								Array.from(selectedSet).filter((option) => optionsToDisplay.includes(option)).length > 0 &&
								Array.from(selectedSet).filter((option) => optionsToDisplay.includes(option)).length <
									optionsToDisplay.length
							}
							onChange={handleSelectAll}
							size="small"
							sx={{
								p: 0.5,
								color: "#666",
								"&.Mui-checked": {
									color: "#000000",
								},
							}}
						/>
						<Typography
							variant="body2"
							sx={{
								fontSize: "0.6rem",
								fontWeight: 500,
								color: "#666666",
								ml: 0.5,
							}}>
							Select All
						</Typography>
					</Box>

					{/* Hint when list is truncated (first 100 only; search to see more) */}
					{isTruncated && (
						<Box sx={{ px: 1, py: 0.5, backgroundColor: "#f5f5f5", borderBottom: "1px solid #e0e0e0" }}>
							<Typography variant="caption" sx={{ fontSize: "0.65rem", color: "#666" }}>
								Showing first {FILTER_DROPDOWN_INITIAL_LIMIT} of {filteredOptions.length}. Search to find more.
							</Typography>
						</Box>
					)}

					{/* Options list */}
					<Box
						sx={{
							maxHeight: "200px",
							overflow: "auto",
							"&::-webkit-scrollbar": {
								width: "6px",
							},
							"&::-webkit-scrollbar-track": {
								backgroundColor: "#f1f1f1",
							},
							"&::-webkit-scrollbar-thumb": {
								backgroundColor: "#c1c1c1",
								borderRadius: "3px",
							},
							"&::-webkit-scrollbar-thumb:hover": {
								backgroundColor: "#a8a8a8",
							},
						}}>
						{optionsToDisplay.length === 0 ? (
							<Typography
								variant="body2"
								sx={{
									color: "#666",
									fontStyle: "italic",
									textAlign: "center",
									py: 2,
									px: 1,
								}}>
								{searchTerm ? "No matching options" : "No options available"}
							</Typography>
						) : (
							optionsToDisplay.map((option) => (
								<Box
									key={option}
									sx={{
										display: "flex",
										alignItems: "center",
										py: 0.5,
										px: 1,
										"&:hover": {
											backgroundColor: "rgba(0, 0, 0, 0.04)",
										},
									}}>
									<Checkbox
										checked={selectedSet.has(option)}
										onChange={(e) => handleCheckboxChange(option, e.target.checked)}
										size="small"
										sx={{
											p: 0.5,
											color: "#666",
											"&.Mui-checked": {
												color: "#000000",
											},
										}}
									/>
									<Typography
										variant="body2"
										sx={{
											fontSize: "0.6rem",
											color: selectedSet.has(option) ? "#000000" : "#666666",
											fontWeight: selectedSet.has(option) ? 500 : 400,
											ml: 0.5,
											flex: 1,
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}>
										{option}
									</Typography>
								</Box>
							))
						)}
					</Box>
				</Box>
			)}
		</Box>
	);
};

export default FilterDropdown;
